import { Injectable, inject, Signal } from '@angular/core';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { IFiDialogResult } from '@fi-shared/fi-dialogs/fi-dialogs';
import {
    IOnboardingCompletion,
    IOnboardingCompletionSaveModel,
    IOnboardingCompletionStep,
    IOnboardingQuizCompletionTimerData,
} from './fi-onboarding-completion';
import { FiOnboardingStepCompletionDialogComponent } from './fi-onboarding-step-completion-dialog/fi-onboarding-step-completion-dialog.component';
import { IOnboardingCompletionUserStep, ITodoItemCompletion } from './fi-onboarding-completion';
import { isNullOrUndefined } from '@fi-shared/utils/is-null-or-undefined';
import { FiOnboardingCompletionStateType, FiOnboardingQuizCompletionStateType } from './fi-onboarding-completion-enums';
import { IQuizEditItem, IQuizQuestionAnswer } from '../fi-quiz/fi-quiz';
import { FiTimeZonesService } from '@fi-app/shared/fi-shared-services/fi-time-zones.service';
import { FiFormatDateService } from '@fi-app/shared/fi-format-date/fi-format-date.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { CommentModel, ICommentModel } from '@fi-app/shared/models';
import { IMediaBankFileReference } from '@fi-app/shared/media-bank';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { ITodoSection } from '@fi-app/fi-onboarding/fi-todo/fi-todo';
import { getObjectTypeNameTextId } from '@fi-app/shared/utils/object-type-name';
import { AppTextService } from '@fi-shared/app-text';
import { FiOnboardingHelperService } from '../fi-onboarding-helper.service';

@Injectable()
export class FiOnboardingCompletionHelperService {
    private readonly fiDialogsService = inject(FiDialogsService);
    private readonly fiTimeZonesService = inject(FiTimeZonesService);
    private readonly fiFormatDateService = inject(FiFormatDateService);
    private readonly userDataService = inject(FiUserDataService);
    private readonly appTextService = inject(AppTextService);
    private readonly fiOnboardingHelperService = inject(FiOnboardingHelperService);

    readonly saveButtonTextIds: Partial<Record<ObjectTypes, string>> = {
        [ObjectTypes.MediaBank]: 'file.mark_as_viewed',
        [ObjectTypes.TodoList]: 'common.save_&_close',
        [ObjectTypes.Quiz]: 'common.submit',
    };

    openStepCompletionDialog(args: {
        onboardingId: number;
        userId: number;
        step: IOnboardingCompletionStep;
        userCompletionStep: IOnboardingCompletionUserStep;
        nextStepCompletionState: FiOnboardingCompletionStateType | undefined;
        isReportMode?: boolean;
    }): IFiDialogResult<IOnboardingCompletionUserStep> {
        return this.fiDialogsService.custom({
            contentComponentRef: FiOnboardingStepCompletionDialogComponent,
            contentComponentData: {
                onboardingId: args.onboardingId,
                userId: args.userId,
                step: args.step,
                userCompletionStep: args.userCompletionStep,
                nextStepCompletionState: args.nextStepCompletionState,
                isReportMode: args.isReportMode,
            },
            hideHeader: true,
            hideFooter: true,
            hideDialogTopBorder: true,
            config: {
                panelClass: 'fi-onboarding-step-completion-dialog-panel',
                width: '800px',
                maxWidth: '90%',
                autoFocus: false,
                disableClose: true,
            },
        });
    }

    /*getFinalQuizCompletionState(
        quiz: IQuizEditItem,
        userCompletionStep: IOnboardingCompletionUserStep,
    ): FiOnboardingQuizCompletionStateType {
        if (isNullOrUndefined(quiz.PassThreshold)) {
            return FiOnboardingQuizCompletionStateType.Completed;
        }
        const correctAnswersCount = this.getCorrectQuizAnswersCount(quiz, userCompletionStep.QuizCompletion.Answers);
        const correctAnswersPercentage = (correctAnswersCount / quiz.Questions.length) * 100;

        if (correctAnswersPercentage >= quiz.PassThreshold) {
            return FiOnboardingQuizCompletionStateType.Completed;
        }

        return FiOnboardingQuizCompletionStateType.Failed;
    }*/

    getCorrectQuizAnswersCount(quiz: IQuizEditItem, answers: IQuizQuestionAnswer[]): number {
        return quiz.Questions.map((question) => {
            const answer = answers.find((answer) => answer.QuizQuestionId === question.Id);
            if (isNullOrUndefined(answer) || isNullOrUndefined(answer.Value) || !answer.Value.length) {
                return 0;
            }
            const correctValues = question.PresetOptions.filter((option) => option.IsCorrect);
            return correctValues.every((option) => answer.Value.includes(option.Value)) &&
                answer.Value.length === correctValues.length
                ? 1
                : 0;
        }).reduce((a, b) => a + b, 0);
    }

    getQuizTimerData(timerMs: number, startDateTime: string): IOnboardingQuizCompletionTimerData {
        //timerMs = 1000 * 10; for tests
        const now = new Date();

        // Use effective start time: if StartDateTime is not set, use current time
        const effectiveStartDateTime = isNullOrUndefined(startDateTime) ? now : new Date(startDateTime);

        // Calculate due date time: startDateTime + TimerMs
        const dueDateTime = new Date(effectiveStartDateTime.getTime() + timerMs);

        return {
            dueDateTimeZone: this.fiTimeZonesService.getGuessTimeZone(),
            maxAvailableTimeMs: timerMs,
            dueDateTime: dueDateTime.toISOString(),
        };
    }

    getOnboardingStepsCount(onboardingCompletion: IOnboardingCompletion): number {
        const onboardingStepsCount = onboardingCompletion.Sections.flatMap((section) => section.Steps).length;
        return onboardingStepsCount;
    }

    ///#region completion save model methods
    getOnboardingCompletionSaveModel(data: {
        onboardingId: number;
        userId: number;
        onboardingStep: IOnboardingCompletionStep;
        userCompletionStep: IOnboardingCompletionUserStep;
    }): IOnboardingCompletionSaveModel {
        return {
            OnboardingId: data.onboardingId,
            Steps: [
                {
                    UserId: data.userId,
                    OnboardingStepId: data.onboardingStep.OnboardingStepId,
                    CompletionState: data.userCompletionStep.CompletionState,
                    ObjectId: data.onboardingStep.ObjectId,
                    ObjectType: data.onboardingStep.ObjectType,
                    TodoItemCompletions: data.userCompletionStep.TodoItemCompletions,
                    QuizCompletion: data.userCompletionStep.QuizCompletion,
                },
            ],
        };
    }

    updateOnboardingCompletionFromSaveResult(
        userCompletionStep: IOnboardingCompletionUserStep,
        saveResult: IOnboardingCompletionSaveModel,
    ) {
        // update on quiz timeout in FiOnboardingCompletionComponent
        this.updateOnboardingCompletionFromSaveResultInternal(
            userCompletionStep,
            saveResult,
            (newCompletionState) => (userCompletionStep.CompletionState = newCompletionState),
            (newQuizCompletionState) => (userCompletionStep.QuizCompletion.CompletionState = newQuizCompletionState),
        );
    }

    updateOnboardingCompletionSignalFromSaveResult(
        userCompletionStep: Signal<IOnboardingCompletionUserStep>,
        saveResult: IOnboardingCompletionSaveModel,
        updateStepCompletionStateFn: (newCompletionState: FiOnboardingCompletionStateType) => void,
        updateQuizCompletionStateFn: (newQuizCompletionState: FiOnboardingQuizCompletionStateType) => void,
    ) {
        // update on quiz timeout and other completions finished in FiOnboardingStepCompletionComponent
        this.updateOnboardingCompletionFromSaveResultInternal(
            userCompletionStep(),
            saveResult,
            updateStepCompletionStateFn,
            updateQuizCompletionStateFn,
        );
    }

    private updateOnboardingCompletionFromSaveResultInternal(
        userCompletionStep: IOnboardingCompletionUserStep,
        saveResult: IOnboardingCompletionSaveModel,
        updateStepCompletionStateFn: (newCompletionState: FiOnboardingCompletionStateType) => void,
        updateQuizCompletionStateFn: (newQuizCompletionState: FiOnboardingQuizCompletionStateType) => void,
    ) {
        const currentCompletionState = userCompletionStep.CompletionState;
        const newCompletionState = saveResult.Steps[0].CompletionState;
        /*console.log(
            'completion onboarding step state from',
            FiOnboardingCompletionStateType[currentCompletionState],
            'to',
            FiOnboardingCompletionStateType[newCompletionState],
        );*/
        // step completion state changed on backend, so should be updated on frontend
        if (currentCompletionState !== newCompletionState) {
            updateStepCompletionStateFn(newCompletionState);
        }

        if (this.fiOnboardingHelperService.isQuizStep(saveResult.Steps[0].ObjectType)) {
            const currentQuizCompletionState = userCompletionStep.QuizCompletion.CompletionState;
            const newQuizCompletionState = saveResult.Steps[0].QuizCompletion.CompletionState;
            /*console.log(
                'completion quiz state from',
                FiOnboardingQuizCompletionStateType[currentQuizCompletionState],
                'to',
                FiOnboardingQuizCompletionStateType[newQuizCompletionState],
            );*/
            // quiz completion state changed on backend, so should be updated on frontend
            if (currentQuizCompletionState !== newQuizCompletionState) {
                updateQuizCompletionStateFn(newQuizCompletionState);
            }
        }
    }
    ///#region completion save model methods

    shouldKeepDialogOpenAfterSave(
        stepObjectType: ObjectTypes,
        quizCompletionState: FiOnboardingQuizCompletionStateType,
    ): boolean {
        if (stepObjectType !== ObjectTypes.Quiz) {
            return false;
        }
        return this.isQuizCompletedOfFailed(quizCompletionState);
    }

    isQuizCompletedOfFailed(quizCompletionState: FiOnboardingQuizCompletionStateType): boolean {
        return [FiOnboardingQuizCompletionStateType.Completed, FiOnboardingQuizCompletionStateType.Failed].includes(
            quizCompletionState,
        );
    }

    /*getStepCompletionStateQuiz(
        allowRetakes: boolean,
        quizCompletionState: FiOnboardingQuizCompletionStateType,
    ): FiOnboardingCompletionStateType {
        // quiz step with failed quiz which allow retakes should have state: InProgress
        return quizCompletionState === FiOnboardingQuizCompletionStateType.Failed && allowRetakes
            ? FiOnboardingCompletionStateType.InProgress
            : FiOnboardingCompletionStateType.Completed;
    }*/

    getStepCompletionStateTodoList(isAllTodoItemsCompleted: boolean): FiOnboardingCompletionStateType {
        return isAllTodoItemsCompleted
            ? FiOnboardingCompletionStateType.Completed
            : FiOnboardingCompletionStateType.InProgress;
    }

    isStepCompleted(userCompletionStep: IOnboardingCompletionUserStep): boolean {
        return userCompletionStep.CompletionState === FiOnboardingCompletionStateType.Completed;
    }

    isStepInProgress(userCompletionStep: IOnboardingCompletionUserStep, allowRetakes: boolean = false): boolean {
        if (userCompletionStep.QuizCompletion?.CompletionState === FiOnboardingQuizCompletionStateType.Failed) {
            if (allowRetakes) {
                return userCompletionStep.CompletionState === FiOnboardingCompletionStateType.InProgress;
            }
            return false;
        }
        return userCompletionStep.CompletionState === FiOnboardingCompletionStateType.InProgress;
    }

    isQuizStepCompletionFailed(userCompletionStep: IOnboardingCompletionUserStep): boolean {
        return userCompletionStep.QuizCompletion?.CompletionState === FiOnboardingQuizCompletionStateType.Failed;
    }

    isQuizStepCompletionNotCompleted(userCompletionStep: IOnboardingCompletionUserStep): boolean {
        return userCompletionStep.QuizCompletion?.CompletionState === FiOnboardingQuizCompletionStateType.NotCompleted;
    }

    isQuizStepCompletionCompleted(userCompletionStep: IOnboardingCompletionUserStep): boolean {
        return userCompletionStep.QuizCompletion?.CompletionState === FiOnboardingQuizCompletionStateType.Completed;
    }

    isQuizStepCompletionInProgress(userCompletionStep: IOnboardingCompletionUserStep): boolean {
        return userCompletionStep.QuizCompletion?.CompletionState === FiOnboardingQuizCompletionStateType.InProgress;
    }

    isQuizStepFailed(userCompletionStep: IOnboardingCompletionUserStep): boolean {
        const isFailedWithRetakes =
            this.isStepInProgress(userCompletionStep) &&
            (this.isQuizStepCompletionFailed(userCompletionStep) ||
                this.isQuizStepCompletionCompleted(userCompletionStep));
        const isFailedOneTime =
            this.isQuizStepCompletionFailed(userCompletionStep) && this.isStepCompleted(userCompletionStep);
        return isFailedWithRetakes || isFailedOneTime;
    }

    private isQuizAllStepsAnswered(
        onboardingStep: IOnboardingCompletionStep,
        userCompletionStep: IOnboardingCompletionUserStep,
    ): boolean {
        const totalQuestionsCount = onboardingStep.Quiz.Questions.length;
        const answeredQuestionsCount = userCompletionStep.QuizCompletion.Answers.filter(
            (answer) => answer.Value?.length > 0,
        ).length;

        return answeredQuestionsCount === totalQuestionsCount;
    }

    isSubmitButtonDisabledOnQuizAnswerChange(
        onboardingStep: IOnboardingCompletionStep,
        userCompletionStep: IOnboardingCompletionUserStep,
    ): boolean {
        return (
            !this.isStepInProgress(userCompletionStep) ||
            !this.isQuizAllStepsAnswered(onboardingStep, userCompletionStep)
        );
    }

    isQuizFullyCompleted(userCompletionStep: IOnboardingCompletionUserStep): boolean {
        return (
            userCompletionStep.CompletionState === FiOnboardingCompletionStateType.Completed &&
            userCompletionStep.QuizCompletion?.CompletionState === FiOnboardingQuizCompletionStateType.Completed
        );
    }

    isQuizNotStarted(userCompletionStep: IOnboardingCompletionUserStep): boolean {
        return (
            this.isQuizStepCompletionNotCompleted(userCompletionStep) &&
            userCompletionStep.CompletionState === FiOnboardingCompletionStateType.NotCompleted
        );
    }

    private isQuizRetakeNotStarted(userCompletionStep: IOnboardingCompletionUserStep): boolean {
        return (
            userCompletionStep.CompletionState === FiOnboardingCompletionStateType.InProgress &&
            userCompletionStep.QuizCompletion?.CompletionState === FiOnboardingQuizCompletionStateType.Failed
        );
    }

    isSaveButtonHiddenQuizStep(userCompletionStep: IOnboardingCompletionUserStep): boolean {
        return (
            this.isQuizStepFailed(userCompletionStep) ||
            this.isQuizFullyCompleted(userCompletionStep) ||
            this.isQuizNotStarted(userCompletionStep) ||
            this.isQuizRetakeNotStarted(userCompletionStep)
        );
    }

    getInitialSubmitButtonState(
        onboardingStep: IOnboardingCompletionStep,
        userCompletionStep: IOnboardingCompletionUserStep,
    ): { isSaveButtonHidden: boolean; isSaveButtonDisabled: boolean } {
        const isStepCompleted = this.isStepCompleted(userCompletionStep);
        const isStepInProgress = this.isStepInProgress(userCompletionStep);

        if (
            this.fiOnboardingHelperService.isDocumentStep(onboardingStep.ObjectType) ||
            this.fiOnboardingHelperService.isMediaBankFileStep(onboardingStep.ObjectType)
        ) {
            return {
                isSaveButtonHidden: isStepCompleted,
                isSaveButtonDisabled: !isStepInProgress,
            };
        }
        if (this.fiOnboardingHelperService.isTodoListStep(onboardingStep.ObjectType)) {
            return {
                isSaveButtonDisabled: false,
                isSaveButtonHidden: true,
            };
        }
        if (this.fiOnboardingHelperService.isQuizStep(onboardingStep.ObjectType)) {
            return {
                isSaveButtonHidden: this.isSaveButtonHiddenQuizStep(userCompletionStep),
                isSaveButtonDisabled:
                    userCompletionStep.QuizCompletion?.CompletionState !==
                    FiOnboardingQuizCompletionStateType.InProgress
                        ? true
                        : this.isSubmitButtonDisabledOnQuizAnswerChange(onboardingStep, userCompletionStep),
            };
        }
    }

    getCompletionItemComments(isCommentAllowed: boolean, completionItem: ITodoItemCompletion): ICommentModel[] {
        if (isCommentAllowed && (isNullOrUndefined(completionItem.Comments) || !completionItem.Comments.length)) {
            const newComment = new CommentModel(this.fiFormatDateService);
            newComment.ContentLanguageId = this.userDataService.userData.PreferredContentLanguage;
            return [newComment];
        }

        return completionItem.Comments;
    }

    getCompletionItemMediaBankFileReferences(
        isAttachmentsAllowed: boolean,
        completionItem: ITodoItemCompletion,
    ): IMediaBankFileReference[] {
        if (isAttachmentsAllowed && isNullOrUndefined(completionItem.MediaBankFileReferences)) {
            return [];
        }
        return completionItem.MediaBankFileReferences;
    }

    normalizeTodoListCompletionStep(
        sections: ITodoSection[],
        userCompletionStep: IOnboardingCompletionUserStep,
    ): IOnboardingCompletionUserStep {
        if (isNullOrUndefined(userCompletionStep.TodoItemCompletions)) {
            console.error('Todo list completion items are not initialized');
            return userCompletionStep;
        }
        const todoItemById = new Map(
            sections.flatMap((section) => section.Items).map((item) => [item.Id, item] as const),
        );
        return {
            ...userCompletionStep,
            TodoItemCompletions: userCompletionStep.TodoItemCompletions.map((completionItem) => {
                const todoItem = todoItemById.get(completionItem.TodoItemId);
                if (isNullOrUndefined(todoItem)) {
                    console.error(`Todo item not found. completion item id: ${completionItem.TodoItemId}`);
                    return completionItem;
                }

                return {
                    ...completionItem,
                    Comments: this.getCompletionItemComments(todoItem.AllowComments, completionItem),
                    MediaBankFileReferences: this.getCompletionItemMediaBankFileReferences(
                        todoItem.AllowAttachments,
                        completionItem,
                    ),
                };
            }),
        };
    }

    showAccessErrorDialog(objectType: ObjectTypes): void {
        this.fiDialogsService.error({
            messageText: this.appTextService.get('common.no_right_to_access_object', {
                '{object_type}': this.appTextService.get(getObjectTypeNameTextId(objectType)).toLowerCase(),
            }),
        });
    }
}
